<template>
  <header class="fn-navbar">
    <!-- 全量菜单按钮 -->
    <button class="fn-navbar__menu-toggle" title="全量菜单" @click="showSidebarDrawer = true">
      <i class="el-icon-s-grid"></i>
    </button>

    <!-- Logo区域 -->
    <div class="fn-navbar__logo" @click="$router.push('/')">
      <div class="fn-navbar__logo-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" opacity="0.9"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="fn-navbar__logo-text">政企PK答题</span>
    </div>

    <!-- 顶部菜单 -->
    <nav class="fn-navbar__menu">
      <template v-for="item in visibleMenuItems">
        <el-dropdown
          v-if="item.children && item.children.length"
          :key="item.authId"
          trigger="hover"
          placement="bottom-start"
          @command="handleMenuClick"
        >
          <div class="fn-navbar__menu-item" :class="{ 'is-active': isMenuActive(item) }">
            <i :class="item.authIcon || 'el-icon-folder'"></i>
            <span>{{ item.authName }}</span>
            <i class="el-icon-arrow-down fn-navbar__menu-arrow"></i>
          </div>
          <el-dropdown-menu slot="dropdown" class="fn-navbar__submenu">
            <el-dropdown-item
              v-for="child in item.children"
              :key="child.authId"
              :command="child.authUrl"
              :class="{ 'is-active': $route.path === child.authUrl }"
            >
              <i :class="child.authIcon || 'el-icon-document'"></i>
              <span>{{ child.authName }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <div
          v-else
          :key="item.authId"
          class="fn-navbar__menu-item"
          :class="{ 'is-active': $route.path === item.authUrl }"
          @click="handleMenuClick(item.authUrl)"
        >
          <i :class="item.authIcon || 'el-icon-document'"></i>
          <span>{{ item.authName }}</span>
        </div>
      </template>

      <!-- 更多菜单 -->
      <el-dropdown
        v-if="hiddenMenuItems.length > 0"
        trigger="hover"
        placement="bottom-start"
        @command="handleMenuClick"
      >
        <div class="fn-navbar__menu-item fn-navbar__menu-more">
          <i class="el-icon-menu"></i>
          <span>更多</span>
          <i class="el-icon-arrow-down fn-navbar__menu-arrow"></i>
        </div>
        <el-dropdown-menu slot="dropdown" class="fn-navbar__submenu fn-navbar__submenu--more">
          <template v-for="item in hiddenMenuItems">
            <el-dropdown-item
              v-if="item.children && item.children.length"
              :key="item.authId"
              disabled
              class="fn-navbar__submenu-group"
            >
              <i :class="item.authIcon || 'el-icon-folder'"></i>
              <span>{{ item.authName }}</span>
            </el-dropdown-item>
            <template v-if="item.children && item.children.length">
              <el-dropdown-item
                v-for="child in item.children"
                :key="child.authId"
                :command="child.authUrl"
                :class="{ 'is-active': $route.path === child.authUrl }"
              >
                <i :class="child.authIcon || 'el-icon-document'"></i>
                <span>{{ child.authName }}</span>
              </el-dropdown-item>
            </template>
            <el-dropdown-item
              v-else
              :key="item.authId"
              :command="item.authUrl"
              :class="{ 'is-active': $route.path === item.authUrl }"
            >
              <i :class="item.authIcon || 'el-icon-document'"></i>
              <span>{{ item.authName }}</span>
            </el-dropdown-item>
          </template>
        </el-dropdown-menu>
      </el-dropdown>
    </nav>

    <!-- 右侧工具区（仅保留用户信息） -->
    <div class="fn-navbar__right">
      <!-- 用户信息 -->
      <el-dropdown trigger="click" class="fn-navbar__user-dropdown" @command="handleAccountCommand">
        <div class="fn-navbar__user">
          <div class="fn-navbar__avatar">{{ avatarText }}</div>
          <span class="fn-navbar__username">{{ userName }}</span>
          <i class="el-icon-arrow-down"></i>
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="switchProject" icon="el-icon-files">切换项目</el-dropdown-item>
          <el-dropdown-item command="password" icon="el-icon-lock">修改密码</el-dropdown-item>
          <el-dropdown-item divided command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 修改密码弹窗（后端无独立改密接口，提交时复用 updateZqpkAuthUser） -->
    <el-dialog title="修改密码" :visible.sync="pwDialogVisible" width="420px" append-to-body v-dialog-drag-resize>
      <el-form ref="pwFormRef" :model="pwForm" :rules="pwRules" label-width="90px">
        <el-form-item label="新密码" prop="newPw">
          <el-input v-model.trim="pwForm.newPw" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPw">
          <el-input v-model.trim="pwForm.confirmPw" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="pwDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="pwSubmitting" @click="handlePwSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 左侧抽屉菜单 -->
    <el-drawer
      :visible.sync="showSidebarDrawer"
      direction="ltr"
      size="320px"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :wrapper-closable="true"
      custom-class="fn-sidebar-drawer"
    >
      <div class="fn-drawer">
        <!-- 顶部品牌区 -->
        <div class="fn-drawer__brand">
          <div class="fn-drawer__brand-bg"></div>
          <div class="fn-drawer__brand-content">
            <div class="fn-drawer__logo">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" opacity="0.9"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="fn-drawer__brand-text">
              <span class="fn-drawer__brand-title">政企PK答题</span>
              <span class="fn-drawer__brand-subtitle">PK ANSWER MANAGEMENT PLATFORM</span>
            </div>
          </div>
          <button class="fn-drawer__close" @click="showSidebarDrawer = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- 完整菜单区 -->
        <div class="fn-drawer__menu-wrapper">
          <div class="fn-drawer__section-title">全部菜单</div>
          <el-menu
            :default-active="$route.path"
            :collapse="false"
            router
            class="fn-drawer__menu"
            @select="showSidebarDrawer = false"
          >
            <template v-for="item in menuTree">
              <el-submenu v-if="item.children && item.children.length" :key="item.authId" :index="String(item.authId)">
                <template slot="title">
                  <div class="fn-drawer__menu-icon">
                    <i :class="item.authIcon || 'el-icon-folder'"></i>
                  </div>
                  <span>{{ item.authName }}</span>
                </template>
                <el-menu-item
                  v-for="child in item.children"
                  :key="child.authId"
                  :index="child.authUrl"
                >
                  <i :class="child.authIcon || 'el-icon-document'"></i>
                  <span>{{ child.authName }}</span>
                </el-menu-item>
              </el-submenu>
              <el-menu-item v-else :key="item.authId" :index="item.authUrl">
                <div class="fn-drawer__menu-icon">
                  <i :class="item.authIcon || 'el-icon-document'"></i>
                </div>
                <span>{{ item.authName }}</span>
              </el-menu-item>
            </template>
          </el-menu>
        </div>

        <!-- 底部用户区 -->
        <div class="fn-drawer__footer">
          <div class="fn-drawer__user">
            <div class="fn-drawer__avatar">{{ avatarText }}</div>
            <div class="fn-drawer__user-info">
              <span class="fn-drawer__username">{{ userName }}</span>
              <span class="fn-drawer__role">{{ roleText }}</span>
            </div>
          </div>
          <button class="fn-drawer__logout" @click="handleLogout" title="退出登录">
            <i class="el-icon-switch-button"></i>
          </button>
        </div>
      </div>
    </el-drawer>
  </header>
</template>

<script>
import { updateZqpkAuthUser } from '@/api/api'

// 岗位字典：1 系统管理员 / 2 项目管理员 / 3 用户
const POST_TEXT = { 1: '系统管理员', 2: '项目管理员', 3: '用户' }

export default {
  name: 'Navbar',
  data () {
    return {
      showSidebarDrawer: false,
      pwDialogVisible: false,
      pwSubmitting: false,
      pwForm: { newPw: '', confirmPw: '' },
      pwRules: {
        newPw: [{ required: true, message: '请输入新密码', trigger: 'blur' }],
        confirmPw: [{
          required: true,
          validator: (rule, value, callback) => {
            if (!value) callback(new Error('请再次输入新密码'))
            else if (value !== this.pwForm.newPw) callback(new Error('两次密码不一致'))
            else callback()
          },
          trigger: 'blur'
        }]
      }
    }
  },
  computed: {
    userInfo () {
      return this.$store.state.user.userInfo || {}
    },
    userName () {
      // 用户名字段：zauName
      return this.userInfo.zauName || '用户'
    },
    roleText () {
      return POST_TEXT[this.userInfo.zauPost] || '用户'
    },
    avatarText () {
      return this.userName.slice(0, 1)
    },
    menuTree () {
      // 菜单树从 permission 模块读取，兼容模块未注册的情况
      const perm = this.$store.state.permission
      return perm ? perm.menuTree || [] : []
    },
    maxVisibleItems () {
      const width = window.innerWidth
      if (width >= 1600) return 8
      if (width >= 1400) return 7
      if (width >= 1200) return 6
      if (width >= 992) return 5
      return 3
    },
    visibleMenuItems () {
      return this.menuTree.slice(0, this.maxVisibleItems)
    },
    hiddenMenuItems () {
      return this.menuTree.slice(this.maxVisibleItems)
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.$forceUpdate()
    },
    handleMenuClick (path) {
      if (path && path !== this.$route.path) {
        this.$router.push(path).catch(() => {})
      }
    },
    isMenuActive (item) {
      if (this.$route.path === item.authUrl) return true
      if (item.children) {
        return item.children.some(child => this.$route.path === child.authUrl)
      }
      return false
    },
    handleAccountCommand (command) {
      if (command === 'logout') {
        this.handleLogout()
        return
      }
      if (command === 'switchProject') {
        this.$router.push('/project-select')
        return
      }
      if (command === 'password') {
        this.pwForm = { newPw: '', confirmPw: '' }
        this.pwDialogVisible = true
        this.$nextTick(() => this.$refs.pwFormRef && this.$refs.pwFormRef.clearValidate())
      }
    },
    // 修改密码提交：后端无独立改密接口，复用 updateZqpkAuthUser 携带当前用户基础信息 + 新密码
    handlePwSubmit () {
      this.$refs.pwFormRef.validate(async valid => {
        if (!valid) return
        this.pwSubmitting = true
        try {
          await updateZqpkAuthUser({
            zauId: this.userInfo.zauId,
            zauName: this.userInfo.zauName,
            zauPhone: this.userInfo.zauPhone,
            zauPost: this.userInfo.zauPost,
            zauPw: this.pwForm.newPw
          })
          this.$message.success('密码修改成功，请重新登录')
          this.pwDialogVisible = false
          this.$store.dispatch('user/logout')
          this.$router.push('/login')
        } catch (e) {} finally {
          this.pwSubmitting = false
        }
      })
    },
    async handleLogout () {
      await this.$confirm('确认退出当前账号吗？', '提示', { type: 'warning' })
      this.$store.dispatch('user/logout')
      // 权限模块重置，兼容模块不存在的情况
      try { this.$store.dispatch('permission/resetPermission') } catch (e) {}
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
.fn-navbar {
  height: 56px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 0 16px 0 12px;
}

.fn-navbar__menu-toggle {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 12px;
  i { font-size: 18px; }
  &:hover { background: #f1f5f9; color: var(--fn-primary); }
}

.fn-navbar__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding-right: 20px;
  border-right: 1px solid #e2e8f0;
  margin-right: 16px;
}

.fn-navbar__logo-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--fn-primary) 0%, var(--fn-accent) 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg { width: 18px; height: 18px; }
}

.fn-navbar__logo-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
}

.fn-navbar__menu {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  overflow: hidden;
}

.fn-navbar__menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid transparent;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);

  &:hover {
    background: rgba(44, 82, 130, 0.08);
    color: var(--fn-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 82, 130, 0.12);
    border-color: rgba(44, 82, 130, 0.15);
    i { color: var(--fn-primary); }
  }

  &.is-active {
    background: linear-gradient(135deg, rgba(44, 82, 130, 0.14) 0%, rgba(214, 158, 46, 0.08) 100%);
    color: var(--fn-primary);
    font-weight: 600;
    border: 1px solid rgba(44, 82, 130, 0.25);
    box-shadow: 0 4px 14px rgba(44, 82, 130, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
    i { color: var(--fn-primary); }

    &::before {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 16px;
      height: 3px;
      background: linear-gradient(90deg, var(--fn-primary) 0%, var(--fn-accent) 100%);
      border-radius: 2px;
      opacity: 0.8;
    }
  }

  i { font-size: 16px; color: #94a3b8; transition: color 0.25s ease; }
}

.fn-navbar__menu-arrow { font-size: 11px !important; margin-left: 2px; opacity: 0.7; }

.fn-navbar__menu-more {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  &:hover { background: #f1f5f9; border-color: #cbd5e1; color: #475569; }
}

.fn-navbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 20px;
  border-left: 1px solid #e5e7eb;
}

.fn-navbar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 8px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #f3f4f6; }
}

.fn-navbar__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--fn-primary) 0%, var(--fn-accent) 100%);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
}

.fn-navbar__username {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style lang="scss">
// 下拉菜单样式
.fn-navbar__submenu {
  margin-top: 4px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }

  .el-dropdown-menu__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 14px;
    color: #475569;
    margin: 3px 0;
    transition: all 0.2s ease;
    overflow: hidden;
    i { font-size: 16px; color: #94a3b8; transition: color 0.2s ease; }

    &:hover {
      background: rgba(44, 82, 130, 0.06);
      color: var(--fn-primary);
      transform: translateX(2px);
      i { color: var(--fn-primary); }
    }

    &.is-active {
      background: linear-gradient(90deg, rgba(44, 82, 130, 0.1) 0%, rgba(214, 158, 46, 0.05) 100%);
      color: var(--fn-primary);
      font-weight: 600;
      border-left: 3px solid var(--fn-primary);
      i { color: var(--fn-primary); }
    }
  }

  .fn-navbar__submenu-group {
    font-weight: 600;
    color: #1e293b !important;
    background: #f1f5f9 !important;
    margin: 6px 0 4px;
    cursor: default;
    border-radius: 6px;
    &:first-child { margin-top: 0; }
  }
}

// 左侧抽屉样式
.fn-sidebar-drawer {
  &.el-drawer__wrapper { .el-drawer { z-index: 2001 !important; } }
  .v-modal { z-index: 2000 !important; }
  .el-drawer__body { padding: 0; overflow: hidden; }

  .fn-drawer {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .fn-drawer__brand {
    position: relative;
    padding: 24px 20px 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
  }

  .fn-drawer__brand-content { display: flex; align-items: center; gap: 14px; }

  .fn-drawer__logo {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, var(--fn-primary) 0%, var(--fn-accent) 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(44, 82, 130, 0.3);
    svg { width: 24px; height: 24px; }
  }

  .fn-drawer__brand-text { display: flex; flex-direction: column; gap: 2px; }
  .fn-drawer__brand-title { font-size: 17px; font-weight: 700; color: #1e293b; letter-spacing: 0.3px; }
  .fn-drawer__brand-subtitle { font-size: 11px; color: #64748b; letter-spacing: 1px; text-transform: uppercase; }

  .fn-drawer__close {
    position: absolute;
    top: 20px;
    right: 16px;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    &:hover { background: #f1f5f9; color: #475569; }
  }

  .fn-drawer__section-title {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 0 20px;
    margin-bottom: 12px;
  }

  .fn-drawer__menu-wrapper {
    flex: 1;
    overflow-y: auto;
    padding: 20px 0;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
  }

  .fn-drawer__menu {
    border-right: none;
    background: transparent;
    padding: 0 16px;

    .el-menu-item, .el-submenu__title {
      height: 44px;
      line-height: 44px;
      margin: 4px 0;
      padding: 0 12px !important;
      border-radius: 10px;
      color: #475569 !important;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.2s;
      &:hover { background: #f8fafc !important; color: #1e293b !important; }
    }

    .el-menu-item.is-active {
      background: linear-gradient(90deg, rgba(44, 82, 130, 0.12) 0%, rgba(214, 158, 46, 0.06) 100%) !important;
      color: var(--fn-primary) !important;
      font-weight: 600;
      border: 1px solid rgba(44, 82, 130, 0.25);
      box-shadow: 0 4px 12px rgba(44, 82, 130, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);
      i { color: var(--fn-primary) !important; }
    }

    .el-submenu {
      .el-menu-item {
        padding-left: 46px !important;
        height: 38px;
        line-height: 38px;
        font-size: 13px;
        color: #64748b !important;
        &.is-active {
          background: linear-gradient(90deg, rgba(44, 82, 130, 0.1) 0%, rgba(214, 158, 46, 0.04) 100%) !important;
          color: var(--fn-primary) !important;
          font-weight: 600;
          border-left: 3px solid var(--fn-primary);
          box-shadow: 0 2px 8px rgba(44, 82, 130, 0.08);
          i { color: var(--fn-primary) !important; }
        }
      }
      .el-submenu__icon-arrow { color: #94a3b8; }
    }
  }

  .fn-drawer__menu-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    i { font-size: 14px; color: #64748b; }
  }

  .el-menu-item:hover .fn-drawer__menu-icon,
  .el-submenu__title:hover .fn-drawer__menu-icon { background: #e2e8f0; }

  .el-menu-item.is-active .fn-drawer__menu-icon {
    background: rgba(44, 82, 130, 0.12);
    i { color: var(--fn-primary) !important; }
  }

  .fn-drawer__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
  }

  .fn-drawer__user { display: flex; align-items: center; gap: 12px; }

  .fn-drawer__avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--fn-primary) 0%, var(--fn-accent) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 600;
    font-size: 15px;
    box-shadow: 0 2px 8px rgba(44, 82, 130, 0.25);
  }

  .fn-drawer__user-info { display: flex; flex-direction: column; gap: 2px; }
  .fn-drawer__username { font-size: 14px; font-weight: 600; color: #1e293b; }
  .fn-drawer__role { font-size: 12px; color: #64748b; }

  .fn-drawer__logout {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: #fef2f2;
    color: #ef4444;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    i { font-size: 18px; }
    &:hover { background: #fecaca; transform: scale(1.05); }
  }
}
</style>
